/**
 * Webtech HR Engine™ - Job Profiles Management Page
 * Gestion des profils de compétences par poste
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  ArrowLeft,
  Plus,
  Search,
  Briefcase,
  MoreVertical,
  Pencil,
  Trash2,
  Eye,
  Award,
  Loader2,
  Building,
  ChevronRight,
  Star,
  AlertCircle,
  CheckCircle2,
  Users,
  Target,
  FileText,
  GripVertical,
  Info,
  Download
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import api from '../lib/api';

// Skill levels
const SKILL_LEVELS = {
  1: { label: 'Débutant', color: 'bg-slate-100 text-slate-700' },
  2: { label: 'Intermédiaire', color: 'bg-blue-100 text-blue-700' },
  3: { label: 'Confirmé', color: 'bg-green-100 text-green-700' },
  4: { label: 'Avancé', color: 'bg-purple-100 text-purple-700' },
  5: { label: 'Expert', color: 'bg-orange-100 text-orange-700' },
};

// Importance levels
const IMPORTANCE_LEVELS = {
  low: { label: 'Faible', color: 'bg-slate-100 text-slate-600' },
  medium: { label: 'Moyen', color: 'bg-yellow-100 text-yellow-700' },
  critical: { label: 'Critique', color: 'bg-red-100 text-red-700' },
};

export default function JobProfilesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const companyId = user?.companyId;
  
  // State
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allSkills, setAllSkills] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  
  // Dialog states
  const [profileDialog, setProfileDialog] = useState({ open: false, mode: 'create', data: null });
  const [viewDialog, setViewDialog] = useState({ open: false, profile: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, profile: null });
  const [bulkAssignDialog, setBulkAssignDialog] = useState({ open: false, profile: null });
  
  // Bulk assign states
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [assignedEmployees, setAssignedEmployees] = useState([]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  
  // Form state
  const [profileForm, setProfileForm] = useState({
    name: '',
    departmentId: '',
    description: '',
    skills: [],
    objectives: [],
    missions: '',
    responsibilities: '',
    requirements: '',
    workingConditions: '',
    hierarchicalPosition: '',
    // Phase 1 — Référentiel carrière
    familyId: '',
    seniorityLevelId: '',
    minYearsExperience: '',
    mandatoryTrainings: [],
    recommendedCertifications: []
  });

  const [selectedSkills, setSelectedSkills] = useState({});
  const [activeFormTab, setActiveFormTab] = useState('general');

  // Phase 1 — Career referential lookups
  const [careerFamilies, setCareerFamilies] = useState([]);
  const [careerSeniorityLevels, setCareerSeniorityLevels] = useState([]);
  const [trainingInput, setTrainingInput] = useState('');
  const [certInput, setCertInput] = useState('');

  useEffect(() => {
    if (companyId) {
      loadData();
    }
  }, [companyId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [profilesRes, categoriesRes, skillsRes, deptRes, famRes, lvlRes] = await Promise.all([
        api.get(`/skills/job-profiles?company_id=${companyId}`),
        api.get(`/skills/categories?company_id=${companyId}`),
        api.get(`/skills/library?company_id=${companyId}`),
        api.get('/departments'),
        api.get('/career/job-families').catch(() => ({ data: { items: [] } })),
        api.get('/career/seniority-levels').catch(() => ({ data: { items: [] } })),
      ]);

      setProfiles(profilesRes.data.profiles || []);
      setCategories(categoriesRes.data.categories || []);
      setAllSkills(skillsRes.data.skills || []);
      setDepartments(deptRes.data.departments || deptRes.data || []);
      setCareerFamilies(famRes.data?.items || []);
      setCareerSeniorityLevels(lvlRes.data?.items || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Open profile dialog
  const openProfileDialog = (mode, profile = null) => {
    if (mode === 'edit' && profile) {
      setProfileForm({
        name: profile.name,
        departmentId: profile.departmentId || '',
        description: profile.description || '',
        skills: profile.skills || [],
        objectives: profile.objectives || [],
        missions: profile.missions || '',
        responsibilities: profile.responsibilities || '',
        requirements: profile.requirements || '',
        workingConditions: profile.workingConditions || '',
        hierarchicalPosition: profile.hierarchicalPosition || '',
        familyId: profile.familyId || '',
        seniorityLevelId: profile.seniorityLevelId || '',
        minYearsExperience: profile.minYearsExperience ?? '',
        mandatoryTrainings: profile.mandatoryTrainings || [],
        recommendedCertifications: profile.recommendedCertifications || []
      });
      // Initialize selected skills
      const selected = {};
      (profile.skills || []).forEach(s => {
        selected[s.skillId] = {
          selected: true,
          requiredLevel: s.requiredLevel || 3,
          importance: s.importance || 'medium'
        };
      });
      setSelectedSkills(selected);
    } else {
      setProfileForm({
        name: '',
        departmentId: '',
        description: '',
        skills: [],
        objectives: [],
        missions: '',
        responsibilities: '',
        requirements: '',
        workingConditions: '',
        hierarchicalPosition: '',
        familyId: '',
        seniorityLevelId: '',
        minYearsExperience: '',
        mandatoryTrainings: [],
        recommendedCertifications: []
      });
      setSelectedSkills({});
    }
    setActiveFormTab('general');
    setTrainingInput('');
    setCertInput('');
    setProfileDialog({ open: true, mode, data: profile });
  };

  // Open bulk assign dialog
  const openBulkAssignDialog = async (profile) => {
    setBulkAssignDialog({ open: true, profile });
    setSelectedEmployeeIds([]);
    setEmployeeSearchTerm('');
    setLoadingEmployees(true);
    
    try {
      const response = await api.get(`/skills/job-profiles/${profile.id}/available-employees?company_id=${companyId}`);
      setAvailableEmployees(response.data.availableEmployees || []);
      setAssignedEmployees(response.data.assignedEmployees || []);
    } catch (error) {
      console.error('Error loading employees:', error);
      toast.error('Erreur lors du chargement des employés');
    } finally {
      setLoadingEmployees(false);
    }
  };

  // Handle bulk assign
  const handleBulkAssign = async () => {
    if (selectedEmployeeIds.length === 0) {
      toast.warning('Veuillez sélectionner au moins un employé');
      return;
    }
    
    setAssigning(true);
    try {
      const response = await api.post(
        `/skills/job-profiles/${bulkAssignDialog.profile.id}/bulk-assign?company_id=${companyId}`,
        { employeeIds: selectedEmployeeIds }
      );
      
      toast.success(response.data.message);
      setBulkAssignDialog({ open: false, profile: null });
      loadData(); // Refresh profiles to update employee count
    } catch (error) {
      console.error('Error assigning profile:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'assignation');
    } finally {
      setAssigning(false);
    }
  };

  // Toggle employee selection
  const toggleEmployeeSelection = (employeeId) => {
    setSelectedEmployeeIds(prev => 
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  // Select/Deselect all employees
  const toggleSelectAllEmployees = () => {
    const filteredEmployees = availableEmployees.filter(emp => {
      if (!employeeSearchTerm) return true;
      const name = `${emp.firstName} ${emp.lastName}`.toLowerCase();
      return name.includes(employeeSearchTerm.toLowerCase());
    });
    
    if (selectedEmployeeIds.length === filteredEmployees.length) {
      setSelectedEmployeeIds([]);
    } else {
      setSelectedEmployeeIds(filteredEmployees.map(e => e.id));
    }
  };

  // Add a new objective
  const addObjective = () => {
    const newObjective = {
      id: `obj_${Date.now()}`,
      title: '',
      description: '',
      specific: '',
      measurable: '',
      achievable: '',
      relevant: '',
      timeBound: '',
      weight: 20
    };
    setProfileForm(prev => ({
      ...prev,
      objectives: [...prev.objectives, newObjective]
    }));
  };

  // Update an objective
  const updateObjective = (index, field, value) => {
    setProfileForm(prev => {
      const newObjectives = [...prev.objectives];
      newObjectives[index] = { ...newObjectives[index], [field]: value };
      return { ...prev, objectives: newObjectives };
    });
  };

  // Remove an objective
  const removeObjective = (index) => {
    setProfileForm(prev => ({
      ...prev,
      objectives: prev.objectives.filter((_, i) => i !== index)
    }));
  };

  // Toggle skill selection
  const toggleSkillSelection = (skillId) => {
    setSelectedSkills(prev => {
      if (prev[skillId]?.selected) {
        const { [skillId]: removed, ...rest } = prev;
        return rest;
      } else {
        return {
          ...prev,
          [skillId]: {
            selected: true,
            requiredLevel: 3,
            importance: 'medium'
          }
        };
      }
    });
  };

  // Update skill requirement
  const updateSkillRequirement = (skillId, field, value) => {
    setSelectedSkills(prev => ({
      ...prev,
      [skillId]: {
        ...prev[skillId],
        [field]: value
      }
    }));
  };

  // Save profile
  const handleSaveProfile = async () => {
    if (!profileForm.name.trim()) {
      toast.error('Le nom de la fiche est requis');
      return;
    }
    
    // Build skills array from selected
    const skills = Object.entries(selectedSkills)
      .filter(([_, data]) => data.selected)
      .map(([skillId, data]) => ({
        skillId,
        requiredLevel: data.requiredLevel,
        importance: data.importance
      }));
    
    // Validate objectives weights
    const totalWeight = profileForm.objectives.reduce((sum, obj) => sum + (obj.weight || 0), 0);
    if (profileForm.objectives.length > 0 && totalWeight !== 100) {
      toast.error(`La somme des pondérations des objectifs doit être 100% (actuellement ${totalWeight}%)`);
      return;
    }
    
    const payload = {
      name: profileForm.name,
      departmentId: profileForm.departmentId === 'none' ? null : profileForm.departmentId,
      description: profileForm.description,
      skills,
      objectives: profileForm.objectives.filter(obj => obj.title.trim()),
      missions: profileForm.missions,
      responsibilities: profileForm.responsibilities,
      requirements: profileForm.requirements,
      workingConditions: profileForm.workingConditions,
      hierarchicalPosition: profileForm.hierarchicalPosition,
      // Phase 1 — Référentiel carrière
      familyId: profileForm.familyId || null,
      seniorityLevelId: profileForm.seniorityLevelId || null,
      minYearsExperience:
        profileForm.minYearsExperience === '' || profileForm.minYearsExperience === null
          ? null
          : Number(profileForm.minYearsExperience),
      mandatoryTrainings: profileForm.mandatoryTrainings || [],
      recommendedCertifications: profileForm.recommendedCertifications || []
    };
    
    try {
      if (profileDialog.mode === 'create') {
        await api.post(`/skills/job-profiles?company_id=${companyId}`, payload);
        toast.success('Fiche créée avec succès');
      } else {
        await api.put(`/skills/job-profiles/${profileDialog.data.id}?company_id=${companyId}`, payload);
        toast.success('Fiche mise à jour');
      }
      setProfileDialog({ open: false, mode: 'create', data: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  // Delete profile
  const handleDeleteProfile = async () => {
    try {
      await api.delete(`/skills/job-profiles/${deleteDialog.profile.id}?company_id=${companyId}`);
      toast.success('Profil supprimé');
      setDeleteDialog({ open: false, profile: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Filter profiles
  const filteredProfiles = profiles.filter(profile => {
    const matchesSearch = profile.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDept = filterDepartment === 'all' || profile.departmentId === filterDepartment;
    return matchesSearch && matchesDept;
  });

  // Check if user is admin
  const isAdmin = user?.role === 'SuperAdmin' || user?.role === 'Admin';

  // Group skills by category for the dialog
  const groupedSkills = categories.map(cat => ({
    ...cat,
    skills: allSkills.filter(skill => {
      const subcat = cat.subcategories?.find(s => s.id === skill.subcategoryId);
      return subcat !== undefined;
    })
  })).filter(cat => cat.skills.length > 0);

  return (
    <DashboardLayout title="Fiches de poste">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <Button variant="ghost" className="mb-2 -ml-2" onClick={() => navigate('/skills')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour aux compétences
            </Button>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Briefcase className="h-7 w-7 text-webtech-blue" />
              Fiches de poste
            </h1>
            <p className="text-slate-500 mt-1">Définissez les compétences et objectifs requis pour chaque poste</p>
          </div>
          
          {isAdmin && (
            <Button onClick={() => openProfileDialog('create')}>
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle fiche
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Fiches définies</p>
                  <p className="text-2xl font-bold text-slate-900">{profiles.length}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Briefcase className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Compétences utilisées</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {new Set(profiles.flatMap(p => p.skills?.map(s => s.skillId) || [])).size}
                  </p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Award className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Départements</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {new Set(profiles.filter(p => p.departmentId).map(p => p.departmentId)).size}
                  </p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Building className="h-5 w-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Moy. compétences/profil</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {profiles.length > 0 ? Math.round(profiles.reduce((sum, p) => sum + (p.skillsCount || 0), 0) / profiles.length) : 0}
                  </p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Target className="h-5 w-5 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Rechercher un profil..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          
          <Select value={filterDepartment} onValueChange={setFilterDepartment}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Tous les départements" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les départements</SelectItem>
              {departments.map(dept => (
                <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Profiles List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-webtech-blue" />
          </div>
        ) : profiles.length === 0 ? (
          <Card className="py-12">
            <CardContent className="text-center">
              <Briefcase className="h-16 w-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucune fiche de poste</h3>
              <p className="text-slate-500 mb-6">
                Créez des fiches pour définir les compétences et objectifs requis par poste.
              </p>
              {isAdmin && (
                <Button onClick={() => openProfileDialog('create')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Créer une fiche
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredProfiles.map(profile => (
              <Card key={profile.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-webtech-blue/10 flex items-center justify-center">
                        <Briefcase className="h-5 w-5 text-webtech-blue" />
                      </div>
                      <div>
                        <CardTitle className="text-base">{profile.name}</CardTitle>
                        {profile.department && (
                          <p className="text-xs text-slate-500">{profile.department.name}</p>
                        )}
                      </div>
                    </div>
                    
                    {isAdmin && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setViewDialog({ open: true, profile })}>
                            <Eye className="h-4 w-4 mr-2" />
                            Voir les détails
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openProfileDialog('edit', profile)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Modifier
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openBulkAssignDialog(profile)}>
                            <Users className="h-4 w-4 mr-2" />
                            Assigner en masse
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => setDeleteDialog({ open: true, profile })}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {profile.description && (
                    <p className="text-sm text-slate-500 mb-4 line-clamp-2">{profile.description}</p>
                  )}
                  
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-1 text-slate-600">
                      <Award className="h-4 w-4" />
                      <span>{profile.skillsCount || 0} compétences</span>
                    </div>
                    
                    {/* Show importance breakdown */}
                    <div className="flex gap-1">
                      {profile.skills?.filter(s => s.importance === 'critical').length > 0 && (
                        <Badge variant="outline" className="text-xs bg-red-50 text-red-600 border-red-200">
                          {profile.skills.filter(s => s.importance === 'critical').length} critiques
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  <Button 
                    variant="outline" 
                    className="w-full mt-4"
                    onClick={() => setViewDialog({ open: true, profile })}
                  >
                    Voir les compétences
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Profile Create/Edit Dialog */}
      <Dialog open={profileDialog.open} onOpenChange={(open) => setProfileDialog({ ...profileDialog, open })}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {profileDialog.mode === 'create' ? 'Nouvelle fiche de poste' : 'Modifier la fiche'}
            </DialogTitle>
            <DialogDescription>
              Définissez les informations, compétences et objectifs requis pour ce poste.
            </DialogDescription>
          </DialogHeader>
          
          <Tabs value={activeFormTab} onValueChange={setActiveFormTab} className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="general">Général</TabsTrigger>
              <TabsTrigger value="objectives">
                Objectifs
                {profileForm.objectives.length > 0 && (
                  <Badge variant="secondary" className="ml-1 text-xs">{profileForm.objectives.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="skills">
                Compétences
                {Object.values(selectedSkills).filter(s => s.selected).length > 0 && (
                  <Badge variant="secondary" className="ml-1 text-xs">{Object.values(selectedSkills).filter(s => s.selected).length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="details">Détails</TabsTrigger>
              <TabsTrigger value="career" data-testid="tab-career">Carrière</TabsTrigger>
            </TabsList>
            
            {/* TAB: Général */}
            <TabsContent value="general" className="space-y-4 mt-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Nom du poste *</Label>
                  <Input
                    value={profileForm.name}
                    onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                    placeholder="Ex: Développeur Senior"
                  />
                </div>
                
                <div>
                  <Label>Département</Label>
                  <Select 
                    value={profileForm.departmentId} 
                    onValueChange={(v) => setProfileForm({ ...profileForm, departmentId: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un département" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun</SelectItem>
                      {departments.map(dept => (
                        <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Description</Label>
                <Textarea
                  value={profileForm.description}
                  onChange={(e) => setProfileForm({ ...profileForm, description: e.target.value })}
                  placeholder="Description générale du poste..."
                  rows={3}
                />
              </div>
              
              <div>
                <Label>Position hiérarchique</Label>
                <Input
                  value={profileForm.hierarchicalPosition}
                  onChange={(e) => setProfileForm({ ...profileForm, hierarchicalPosition: e.target.value })}
                  placeholder="Ex: Rattaché au Directeur Technique, encadre 3 développeurs juniors"
                />
              </div>
            </TabsContent>
            
            {/* TAB: Objectifs SMART */}
            <TabsContent value="objectives" className="space-y-4 mt-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Target className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-900">Objectifs SMART</p>
                    <p className="text-sm text-amber-700">
                      Les objectifs définis ici seront utilisés lors des évaluations de performance des employés occupant ce poste.
                      La somme des pondérations doit être égale à 100%.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Objectives List */}
              {profileForm.objectives.length === 0 ? (
                <div className="text-center py-8 bg-slate-50 rounded-lg border-2 border-dashed">
                  <Target className="h-12 w-12 mx-auto text-slate-300 mb-2" />
                  <p className="text-slate-500 mb-4">Aucun objectif défini</p>
                  <Button onClick={addObjective} variant="outline">
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter un objectif
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {profileForm.objectives.map((objective, index) => (
                    <Card key={objective.id || index} className="border-l-4 border-l-indigo-500">
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-indigo-600">
                              Objectif {index + 1}
                            </Badge>
                            <Badge className="bg-indigo-100 text-indigo-700">
                              {objective.weight}%
                            </Badge>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => removeObjective(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <div className="space-y-3">
                          <div className="grid gap-3 md:grid-cols-4">
                            <div className="md:col-span-3">
                              <Label className="text-xs text-slate-500">Titre de l'objectif *</Label>
                              <Input
                                value={objective.title}
                                onChange={(e) => updateObjective(index, 'title', e.target.value)}
                                placeholder="Ex: Améliorer la satisfaction client"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-slate-500">Pondération (%)</Label>
                              <Input
                                type="number"
                                min={1}
                                max={100}
                                value={objective.weight}
                                onChange={(e) => updateObjective(index, 'weight', parseInt(e.target.value) || 0)}
                              />
                            </div>
                          </div>
                          
                          <div>
                            <Label className="text-xs text-slate-500">Description</Label>
                            <Textarea
                              value={objective.description}
                              onChange={(e) => updateObjective(index, 'description', e.target.value)}
                              placeholder="Description détaillée de l'objectif"
                              rows={2}
                            />
                          </div>
                          
                          {/* SMART Criteria */}
                          <div className="grid gap-3 md:grid-cols-2">
                            <div>
                              <Label className="text-xs text-slate-500 flex items-center gap-1">
                                <span className="font-bold text-indigo-600">S</span>pécifique
                              </Label>
                              <Input
                                value={objective.specific}
                                onChange={(e) => updateObjective(index, 'specific', e.target.value)}
                                placeholder="Quoi exactement ?"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-slate-500 flex items-center gap-1">
                                <span className="font-bold text-indigo-600">M</span>esurable
                              </Label>
                              <Input
                                value={objective.measurable}
                                onChange={(e) => updateObjective(index, 'measurable', e.target.value)}
                                placeholder="Indicateurs de mesure"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-slate-500 flex items-center gap-1">
                                <span className="font-bold text-indigo-600">A</span>tteignable
                              </Label>
                              <Input
                                value={objective.achievable}
                                onChange={(e) => updateObjective(index, 'achievable', e.target.value)}
                                placeholder="Ressources disponibles"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-slate-500 flex items-center gap-1">
                                <span className="font-bold text-indigo-600">R</span>éaliste
                              </Label>
                              <Input
                                value={objective.relevant}
                                onChange={(e) => updateObjective(index, 'relevant', e.target.value)}
                                placeholder="Pertinence stratégique"
                              />
                            </div>
                            <div className="md:col-span-2">
                              <Label className="text-xs text-slate-500 flex items-center gap-1">
                                <span className="font-bold text-indigo-600">T</span>emporel
                              </Label>
                              <Input
                                value={objective.timeBound}
                                onChange={(e) => updateObjective(index, 'timeBound', e.target.value)}
                                placeholder="Échéance ou délai"
                              />
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  
                  <Button onClick={addObjective} variant="outline" className="w-full">
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter un objectif
                  </Button>
                  
                  {/* Weight summary */}
                  {profileForm.objectives.length > 0 && (
                    <div className={`p-3 rounded-lg flex items-center justify-between ${
                      profileForm.objectives.reduce((sum, obj) => sum + (obj.weight || 0), 0) === 100
                        ? 'bg-green-50 border border-green-200'
                        : 'bg-red-50 border border-red-200'
                    }`}>
                      <span className="text-sm font-medium">
                        Total des pondérations: {profileForm.objectives.reduce((sum, obj) => sum + (obj.weight || 0), 0)}%
                      </span>
                      {profileForm.objectives.reduce((sum, obj) => sum + (obj.weight || 0), 0) === 100 ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-red-600" />
                      )}
                    </div>
                  )}
                </div>
              )}
            </TabsContent>
            
            {/* TAB: Compétences */}
            <TabsContent value="skills" className="space-y-4 mt-4">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <Label className="text-base font-semibold">Compétences requises</Label>
                    <p className="text-sm text-slate-500">Sélectionnez les compétences et définissez le niveau requis.</p>
                  </div>
                  {allSkills.length === 0 && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={async () => {
                        try {
                          await api.post(`/skills/seed-defaults?company_id=${companyId}`);
                          toast.success('Compétences par défaut importées !');
                          loadData();
                        } catch (error) {
                          toast.error('Erreur lors de l\'import');
                        }
                      }}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Importer compétences par défaut
                    </Button>
                  )}
                </div>
                
                {allSkills.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
                    <Award className="h-12 w-12 mx-auto text-slate-300 mb-2" />
                    <p className="text-slate-500 mb-3">Aucune compétence disponible dans la bibliothèque.</p>
                    <div className="flex justify-center gap-2">
                      <Button 
                        variant="outline" 
                        onClick={async () => {
                          try {
                            await api.post(`/skills/seed-defaults?company_id=${companyId}`);
                            toast.success('Compétences par défaut importées !');
                            loadData();
                          } catch (error) {
                            toast.error('Erreur lors de l\'import');
                          }
                        }}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Importer compétences par défaut
                      </Button>
                      <Button variant="link" onClick={() => navigate('/skills')}>
                        Gérer la bibliothèque
                      </Button>
                    </div>
                  </div>
                ) : groupedSkills.length === 0 ? (
                  /* Cas où il y a des compétences mais pas groupées par catégorie */
                  <div className="border rounded-lg divide-y max-h-96 overflow-y-auto">
                    <div className="p-3 bg-slate-50 font-medium text-sm flex items-center gap-2 border-l-4 border-indigo-500">
                      <Award className="h-4 w-4" />
                      Toutes les compétences
                      <Badge variant="secondary" className="text-xs">{allSkills.length}</Badge>
                    </div>
                    {allSkills.map(skill => (
                      <div key={skill.id} className="p-3 hover:bg-slate-50">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={selectedSkills[skill.id]?.selected || false}
                            onCheckedChange={() => toggleSkillSelection(skill.id)}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-sm">{skill.name}</p>
                                {skill.description && (
                                  <p className="text-xs text-slate-500">{skill.description}</p>
                                )}
                              </div>
                            </div>
                            
                            {selectedSkills[skill.id]?.selected && (
                              <div className="mt-3 grid gap-4 md:grid-cols-2">
                                <div>
                                  <Label className="text-xs">Niveau requis</Label>
                                  <div className="flex items-center gap-2 mt-1">
                                    <Slider
                                      value={[selectedSkills[skill.id]?.requiredLevel || 3]}
                                      onValueChange={([v]) => updateSkillRequirement(skill.id, 'requiredLevel', v)}
                                      min={1}
                                      max={5}
                                      step={1}
                                      className="flex-1"
                                    />
                                    <Badge className={SKILL_LEVELS[selectedSkills[skill.id]?.requiredLevel || 3].color}>
                                      {SKILL_LEVELS[selectedSkills[skill.id]?.requiredLevel || 3].label}
                                    </Badge>
                                  </div>
                                </div>
                                
                                <div>
                                  <Label className="text-xs">Importance</Label>
                                  <Select 
                                    value={selectedSkills[skill.id]?.importance || 'medium'}
                                    onValueChange={(v) => updateSkillRequirement(skill.id, 'importance', v)}
                                  >
                                    <SelectTrigger className="mt-1">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="low">Faible</SelectItem>
                                      <SelectItem value="medium">Moyen</SelectItem>
                                      <SelectItem value="critical">Critique</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border rounded-lg divide-y max-h-96 overflow-y-auto">
                    {groupedSkills.map(category => (
                      <div key={category.id}>
                        <div 
                          className="p-3 bg-slate-50 font-medium text-sm flex items-center gap-2"
                          style={{ borderLeft: `4px solid ${category.color}` }}
                        >
                          {category.name}
                          <Badge variant="secondary" className="text-xs">
                            {category.skills.length}
                          </Badge>
                        </div>
                        
                        {category.skills.map(skill => (
                          <div key={skill.id} className="p-3 hover:bg-slate-50">
                            <div className="flex items-start gap-3">
                              <Checkbox
                                checked={selectedSkills[skill.id]?.selected || false}
                                onCheckedChange={() => toggleSkillSelection(skill.id)}
                                className="mt-1"
                              />
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-medium text-sm">{skill.name}</p>
                                    {skill.description && (
                                      <p className="text-xs text-slate-500">{skill.description}</p>
                                    )}
                                  </div>
                                </div>
                                
                                {selectedSkills[skill.id]?.selected && (
                                  <div className="mt-3 grid gap-4 md:grid-cols-2">
                                    <div>
                                      <Label className="text-xs">Niveau requis</Label>
                                      <div className="flex items-center gap-2 mt-1">
                                        <Slider
                                          value={[selectedSkills[skill.id]?.requiredLevel || 3]}
                                          onValueChange={([v]) => updateSkillRequirement(skill.id, 'requiredLevel', v)}
                                          min={1}
                                          max={5}
                                          step={1}
                                          className="flex-1"
                                        />
                                        <Badge className={SKILL_LEVELS[selectedSkills[skill.id]?.requiredLevel || 3].color}>
                                          {SKILL_LEVELS[selectedSkills[skill.id]?.requiredLevel || 3].label}
                                        </Badge>
                                      </div>
                                    </div>
                                    
                                    <div>
                                      <Label className="text-xs">Importance</Label>
                                      <Select 
                                        value={selectedSkills[skill.id]?.importance || 'medium'}
                                        onValueChange={(v) => updateSkillRequirement(skill.id, 'importance', v)}
                                      >
                                        <SelectTrigger className="mt-1">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="low">Faible</SelectItem>
                                          <SelectItem value="medium">Moyen</SelectItem>
                                          <SelectItem value="critical">Critique</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="flex items-center justify-between mt-2">
                  <p className="text-sm text-slate-500">
                    {Object.values(selectedSkills).filter(s => s.selected).length} compétence(s) sélectionnée(s)
                  </p>
                  <Button variant="link" size="sm" onClick={() => navigate('/skills')}>
                    <Plus className="h-4 w-4 mr-1" />
                    Gérer la bibliothèque
                  </Button>
                </div>
              </div>
            </TabsContent>
            
            {/* TAB: Détails */}
            <TabsContent value="details" className="space-y-4 mt-4">
              <div>
                <Label>Missions principales</Label>
                <Textarea
                  value={profileForm.missions}
                  onChange={(e) => setProfileForm({ ...profileForm, missions: e.target.value })}
                  placeholder="Décrivez les missions principales du poste..."
                  rows={3}
                />
              </div>
              
              <div>
                <Label>Responsabilités</Label>
                <Textarea
                  value={profileForm.responsibilities}
                  onChange={(e) => setProfileForm({ ...profileForm, responsibilities: e.target.value })}
                  placeholder="Listez les responsabilités clés..."
                  rows={3}
                />
              </div>
              
              <div>
                <Label>Pré-requis (diplômes, expérience)</Label>
                <Textarea
                  value={profileForm.requirements}
                  onChange={(e) => setProfileForm({ ...profileForm, requirements: e.target.value })}
                  placeholder="Ex: Bac+5 en informatique, 5 ans d'expérience..."
                  rows={2}
                />
              </div>
              
              <div>
                <Label>Conditions de travail</Label>
                <Textarea
                  value={profileForm.workingConditions}
                  onChange={(e) => setProfileForm({ ...profileForm, workingConditions: e.target.value })}
                  placeholder="Ex: Travail en open space, déplacements ponctuels..."
                  rows={2}
                />
              </div>
            </TabsContent>

            {/* === ONGLET CARRIÈRE === */}
            <TabsContent value="career" className="space-y-4 mt-4">
              <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-3 text-xs text-indigo-700">
                Rattachez ce poste à votre référentiel de carrière (famille métier, niveau séniorité) et précisez
                les exigences (expérience minimale, formations obligatoires, certifications recommandées).
                Ces informations alimentent les analyses GPEC et les recommandations de mobilité.
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Famille métier</Label>
                  <Select
                    value={profileForm.familyId || 'none'}
                    onValueChange={(v) => setProfileForm({ ...profileForm, familyId: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger data-testid="career-family-select">
                      <SelectValue placeholder="Aucune" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune</SelectItem>
                      {careerFamilies.map(f => (
                        <SelectItem key={f.id} value={f.id} data-testid={`career-family-${f.id}`}>
                          {f.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {careerFamilies.length === 0 && (
                    <p className="text-xs text-slate-400 mt-1">
                      Créez vos familles depuis Talents → Référentiel carrière
                    </p>
                  )}
                </div>

                <div>
                  <Label>Niveau de séniorité</Label>
                  <Select
                    value={profileForm.seniorityLevelId || 'none'}
                    onValueChange={(v) => setProfileForm({ ...profileForm, seniorityLevelId: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger data-testid="career-seniority-select">
                      <SelectValue placeholder="Aucun" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun</SelectItem>
                      {careerSeniorityLevels.map(l => (
                        <SelectItem key={l.id} value={l.id} data-testid={`career-lvl-${l.id}`}>
                          {l.name} {l.minYearsExperience > 0 && `(≥ ${l.minYearsExperience} ans)`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-2">
                  <Label>Années minimum d'expérience</Label>
                  <Input
                    type="number"
                    min={0}
                    max={50}
                    value={profileForm.minYearsExperience}
                    onChange={(e) => setProfileForm({ ...profileForm, minYearsExperience: e.target.value })}
                    placeholder="ex : 3"
                    data-testid="career-min-years-input"
                    className="max-w-xs"
                  />
                </div>
              </div>

              <div>
                <Label>Formations obligatoires</Label>
                <p className="text-xs text-slate-500 mb-2">Saisissez une formation puis appuyez sur Entrée</p>
                <div className="flex gap-2">
                  <Input
                    value={trainingInput}
                    onChange={(e) => setTrainingInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const v = trainingInput.trim();
                        if (v && !profileForm.mandatoryTrainings.includes(v)) {
                          setProfileForm({
                            ...profileForm,
                            mandatoryTrainings: [...profileForm.mandatoryTrainings, v],
                          });
                        }
                        setTrainingInput('');
                      }
                    }}
                    placeholder="ex : Sécurité au travail (SST)"
                    data-testid="career-training-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const v = trainingInput.trim();
                      if (v && !profileForm.mandatoryTrainings.includes(v)) {
                        setProfileForm({
                          ...profileForm,
                          mandatoryTrainings: [...profileForm.mandatoryTrainings, v],
                        });
                      }
                      setTrainingInput('');
                    }}
                    data-testid="career-add-training-btn"
                  >
                    Ajouter
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1 mt-2" data-testid="career-trainings-list">
                  {profileForm.mandatoryTrainings.map((t, idx) => (
                    <Badge
                      key={idx}
                      variant="outline"
                      className="bg-amber-50 text-amber-700 border-amber-200 pr-1"
                    >
                      {t}
                      <button
                        type="button"
                        onClick={() => setProfileForm({
                          ...profileForm,
                          mandatoryTrainings: profileForm.mandatoryTrainings.filter((_, i) => i !== idx),
                        })}
                        className="ml-1.5 hover:text-red-600"
                        aria-label="Retirer"
                      >×</button>
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <Label>Certifications recommandées</Label>
                <p className="text-xs text-slate-500 mb-2">Saisissez une certification puis appuyez sur Entrée</p>
                <div className="flex gap-2">
                  <Input
                    value={certInput}
                    onChange={(e) => setCertInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const v = certInput.trim();
                        if (v && !profileForm.recommendedCertifications.includes(v)) {
                          setProfileForm({
                            ...profileForm,
                            recommendedCertifications: [...profileForm.recommendedCertifications, v],
                          });
                        }
                        setCertInput('');
                      }
                    }}
                    placeholder="ex : PMP, ITIL, AWS Solutions Architect..."
                    data-testid="career-cert-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const v = certInput.trim();
                      if (v && !profileForm.recommendedCertifications.includes(v)) {
                        setProfileForm({
                          ...profileForm,
                          recommendedCertifications: [...profileForm.recommendedCertifications, v],
                        });
                      }
                      setCertInput('');
                    }}
                    data-testid="career-add-cert-btn"
                  >
                    Ajouter
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1 mt-2" data-testid="career-certs-list">
                  {profileForm.recommendedCertifications.map((t, idx) => (
                    <Badge
                      key={idx}
                      variant="outline"
                      className="bg-indigo-50 text-indigo-700 border-indigo-200 pr-1"
                    >
                      {t}
                      <button
                        type="button"
                        onClick={() => setProfileForm({
                          ...profileForm,
                          recommendedCertifications: profileForm.recommendedCertifications.filter((_, i) => i !== idx),
                        })}
                        className="ml-1.5 hover:text-red-600"
                        aria-label="Retirer"
                      >×</button>
                    </Badge>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setProfileDialog({ ...profileDialog, open: false })}>
              Annuler
            </Button>
            <Button onClick={handleSaveProfile}>
              {profileDialog.mode === 'create' ? 'Créer la fiche' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Profile Dialog */}
      <Dialog open={viewDialog.open} onOpenChange={(open) => setViewDialog({ ...viewDialog, open })}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-webtech-blue/10 flex items-center justify-center">
                <Briefcase className="h-5 w-5 text-webtech-blue" />
              </div>
              <div>
                <span className="text-xl">{viewDialog.profile?.name}</span>
                {viewDialog.profile?.department && (
                  <p className="text-sm font-normal text-slate-500 mt-0.5">
                    {viewDialog.profile.department.name}
                  </p>
                )}
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {viewDialog.profile?.description && (
            <p className="text-sm text-slate-600 py-3 border-b">{viewDialog.profile.description}</p>
          )}
          
          <div className="flex-1 overflow-y-auto py-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-slate-900">
                Compétences requises ({viewDialog.profile?.skills?.length || 0})
              </h4>
              <div className="flex gap-2 text-xs">
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-red-500"></span> Critique
                </span>
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-amber-500"></span> Important
                </span>
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-slate-400"></span> Moyen
                </span>
              </div>
            </div>
            
            {viewDialog.profile?.skills?.length > 0 ? (
              <div className="grid gap-2">
                {viewDialog.profile.skills.map((skillReq, idx) => {
                  const skillName = skillReq.skillName || skillReq.skill?.name || 'Compétence inconnue';
                  const importanceColor = skillReq.importance === 'critical' 
                    ? 'border-l-red-500 bg-red-50/50' 
                    : skillReq.importance === 'important' 
                    ? 'border-l-amber-500 bg-amber-50/50' 
                    : 'border-l-slate-300 bg-slate-50/50';
                  
                  return (
                    <div 
                      key={idx}
                      className={`flex items-center justify-between p-3 rounded-lg border-l-4 ${importanceColor}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-white border flex items-center justify-center text-sm font-medium text-slate-600">
                          {idx + 1}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{skillName}</p>
                          {(skillReq.category?.name || skillReq.subcategory?.name) && (
                            <p className="text-xs text-slate-500">
                              {[skillReq.category?.name, skillReq.subcategory?.name].filter(Boolean).join(' → ')}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={SKILL_LEVELS[skillReq.requiredLevel]?.color || 'bg-slate-100 text-slate-700'}>
                          Niveau {skillReq.requiredLevel} - {SKILL_LEVELS[skillReq.requiredLevel]?.label || 'N/A'}
                        </Badge>
                        <Badge variant="outline" className={
                          skillReq.importance === 'critical' ? 'border-red-300 text-red-700' :
                          skillReq.importance === 'important' ? 'border-amber-300 text-amber-700' :
                          'border-slate-300 text-slate-600'
                        }>
                          {IMPORTANCE_LEVELS[skillReq.importance]?.label || skillReq.importance || 'Moyen'}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <Briefcase className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                <p>Aucune compétence définie pour ce profil</p>
                <p className="text-sm">Modifiez le profil pour ajouter des compétences requises</p>
              </div>
            )}
          </div>
          
          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setViewDialog({ open: false, profile: null })}>
              Fermer
            </Button>
            {isAdmin && (
              <Button onClick={() => {
                setViewDialog({ open: false, profile: null });
                openProfileDialog('edit', viewDialog.profile);
              }}>
                <Pencil className="h-4 w-4 mr-2" />
                Modifier le profil
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Supprimer la fiche de poste</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer la fiche <strong>"{deleteDialog.profile?.name}"</strong> ?
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteProfile} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Assign Dialog */}
      <Dialog open={bulkAssignDialog.open} onOpenChange={(open) => setBulkAssignDialog({ ...bulkAssignDialog, open })}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              Assigner en masse : {bulkAssignDialog.profile?.name}
            </DialogTitle>
            <DialogDescription>
              Sélectionnez les employés auxquels vous souhaitez assigner cette fiche de poste.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Rechercher un employé..."
                value={employeeSearchTerm}
                onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Stats */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">
                {selectedEmployeeIds.length} employé(s) sélectionné(s)
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleSelectAllEmployees}
                className="text-blue-600"
              >
                {selectedEmployeeIds.length === availableEmployees.filter(emp => {
                  if (!employeeSearchTerm) return true;
                  const name = `${emp.firstName} ${emp.lastName}`.toLowerCase();
                  return name.includes(employeeSearchTerm.toLowerCase());
                }).length ? 'Tout désélectionner' : 'Tout sélectionner'}
              </Button>
            </div>
            
            {/* Already assigned info */}
            {assignedEmployees.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700">
                  <strong>{assignedEmployees.length}</strong> employé(s) ont déjà cette fiche assignée
                </p>
              </div>
            )}
            
            {/* Employee list */}
            <div className="border rounded-lg overflow-hidden max-h-[300px] overflow-y-auto">
              {loadingEmployees ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : availableEmployees.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  Aucun employé disponible pour cette assignation
                </div>
              ) : (
                <div className="divide-y">
                  {availableEmployees
                    .filter(emp => {
                      if (!employeeSearchTerm) return true;
                      const name = `${emp.firstName} ${emp.lastName}`.toLowerCase();
                      return name.includes(employeeSearchTerm.toLowerCase());
                    })
                    .map(employee => (
                      <label
                        key={employee.id}
                        className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 transition-colors ${
                          selectedEmployeeIds.includes(employee.id) ? 'bg-blue-50' : ''
                        }`}
                      >
                        <Checkbox
                          checked={selectedEmployeeIds.includes(employee.id)}
                          onCheckedChange={() => toggleEmployeeSelection(employee.id)}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900">
                            {employee.firstName} {employee.lastName}
                          </p>
                          <p className="text-sm text-slate-500 truncate">
                            {employee.position || 'Poste non défini'} 
                            {employee.department && ` • ${employee.department}`}
                          </p>
                          {employee.jobProfileName && (
                            <Badge variant="outline" className="mt-1 text-xs">
                              Fiche actuelle: {employee.jobProfileName}
                            </Badge>
                          )}
                        </div>
                      </label>
                    ))
                  }
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBulkAssignDialog({ open: false, profile: null })}
            >
              Annuler
            </Button>
            <Button
              onClick={handleBulkAssign}
              disabled={assigning || selectedEmployeeIds.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-bulk-assign-btn"
            >
              {assigning ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Assignation...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Assigner ({selectedEmployeeIds.length})
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
